import {WriteButton} from '../common/buttons/WriteActions';
import AddIcon from '@mui/icons-material/Add';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import useDebounce from '../../hooks/useDebounce';
import {usePageLimit} from '../../hooks/usePageLimit';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {FinanceGroupBy, IFinanceExpenseBreakdown, IFinanceTransaction} from '@mybake/ui/models/Finances/IFinance';
import {financeAPI} from '../../services/FinanceServices';
import {FinanceChart} from '@mybake/ui/components/finances/FinanceChart';
import {FinanceDateField} from './FinanceDateField';
import {FinanceTransactions} from './FinanceTransactions';
import {ManualExpenseDialog} from './ManualExpenseDialog';
import {useCurrency} from '../../hooks/useCurrency';
import {getApiErrorMessage, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {PageHeader} from '../common/layout/PageHeader';

const toLocalDate = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const getInitialPeriod = () => {
    const now = new Date();
    return {
        from: toLocalDate(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: toLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        groupBy: 'DAY' as FinanceGroupBy,
    };
};

const MIN_FINANCE_DATE = '1900-01-01';
const MAX_FINANCE_DATE = '2100-12-31';

const toFinanceDateDisplay = (value: string) => {
    const [year, month, day] = value.split('-');
    return `${day}-${month}-${year}`;
};

const financeDateToIso = (value: string) => {
    const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
    if (!match) return null;

    const [, dayValue, monthValue, yearValue] = match;
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;

    const isoDate = `${yearValue}-${monthValue}-${dayValue}`;
    return isoDate >= MIN_FINANCE_DATE && isoDate <= MAX_FINANCE_DATE ? isoDate : null;
};

const BreakdownRows = ({
    rows,
    total,
    currency,
    emptyText,
}: {
    rows: IFinanceExpenseBreakdown[];
    total: number;
    currency: (value: number) => string;
    emptyText: string;
}) => {
    if (!rows.length) return <Typography color="text.secondary">{emptyText}</Typography>;

    return (
        <Stack spacing={1.5}>
            {rows.slice(0, 6).map((row) => (
                <Box key={row.category}>
                    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{mb: 0.5}}>
                        <Typography variant="body2">{row.category}</Typography>
                        <Typography variant="body2" fontWeight={700}>
                            {currency(row.amount)}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={total ? Math.min(100, (row.amount / total) * 100) : 0}
                        sx={{height: 6, borderRadius: 999}}
                    />
                </Box>
            ))}
        </Stack>
    );
};

const Finances = () => {
    const {t, i18n} = useAppTranslation();
    const locale = i18n.resolvedLanguage ?? i18n.language ?? 'pl';
    const {currency: selectedCurrency} = useCurrency();
    const [period, setPeriod] = useState(getInitialPeriod);
    const [dateDraft, setDateDraft] = useState(() => {
        const initialPeriod = getInitialPeriod();
        return {from: toFinanceDateDisplay(initialPeriod.from), to: toFinanceDateDisplay(initialPeriod.to)};
    });
    const debouncedDates = useDebounce(dateDraft, 600);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = usePageLimit('finances');
    const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState<IFinanceTransaction | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<IFinanceTransaction | null>(null);
    const [notification, setNotification] = useState('');
    const [notificationSeverity, setNotificationSeverity] = useState<'success' | 'error'>('success');
    const [notificationTraceId, setNotificationTraceId] = useState('');

    const summaryQuery = financeAPI.useGetFinanceSummaryQuery(period);
    const transactionsQuery = financeAPI.useGetFinanceTransactionsQuery({...period, page, pageSize});
    const [deleteExpense, deleteState] = financeAPI.useDeleteManualExpenseMutation();
    const totals = summaryQuery.data?.totals;
    const draftFromIso = financeDateToIso(dateDraft.from);
    const draftToIso = financeDateToIso(dateDraft.to);
    const isFromValid = !!draftFromIso;
    const isToValid = !!draftToIso;
    const isRangeValid = !!draftFromIso && !!draftToIso && draftFromIso <= draftToIso;

    useEffect(() => {
        const from = financeDateToIso(debouncedDates.from);
        const to = financeDateToIso(debouncedDates.to);
        if (!from || !to || from > to) return;

        setPeriod((value) =>
            value.from === from && value.to === to
                ? value
                : {...value, from, to},
        );
    }, [debouncedDates]);

    useEffect(() => setPage(0), [period.from, period.to, period.groupBy]);

    const currency = useMemo(
        () => (value: number) =>
            new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: selectedCurrency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value || 0),
        [locale, selectedCurrency],
    );

    const costRows: IFinanceExpenseBreakdown[] = totals
        ? [
              {category: t('finance:ingredients'), amount: totals.orderIngredientCost, percent: 0},
              {category: t('finance:packaging'), amount: totals.orderPackageCost, percent: 0},
              ...(totals.unallocatedOrderCost > 0
                  ? [{category: t('finance:unallocated_cost'), amount: totals.unallocatedOrderCost, percent: 0}]
                  : []),
          ]
        : [];

    const purchaseRows: IFinanceExpenseBreakdown[] = totals
        ? [
              {category: t('finance:ingredient_purchases'), amount: totals.productPurchases, percent: 0},
              {category: t('finance:package_purchases'), amount: totals.packagePurchases, percent: 0},
          ]
        : [];

    const openNewExpense = () => {
        setSelectedExpense(null);
        setExpenseDialogOpen(true);
    };

    const openEditExpense = (transaction: IFinanceTransaction) => {
        setSelectedExpense(transaction);
        setExpenseDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const id = deleteTarget.id.startsWith('expense:') ? deleteTarget.id.slice('expense:'.length) : deleteTarget.id;
        try {
            await deleteExpense(id).unwrap();
            setNotificationSeverity('success');
            setNotification(t('finance:expense_deleted'));
            setDeleteTarget(null);
        } catch (error) {
            setNotificationSeverity('error');
            const traceId = getApiTraceId(error);
            setNotification(getApiErrorMessage(error, t));
            setNotificationTraceId(traceId);
        }
    };

    return (
        <Box sx={{minHeight: '100%'}}>
            <PageHeader title={t('finance:title')} subtitle={t('finance:subtitle')} action={<WriteButton variant="contained" startIcon={<AddIcon/>} onClick={openNewExpense}>
                    {t('finance:add_expense')}
                </WriteButton>}/>

            <Paper sx={{p: 2, mb: 3}}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <FinanceDateField
                            label={t('finance:period_from')}
                            value={dateDraft.from}
                            error={!isFromValid || (isFromValid && isToValid && !isRangeValid)}
                            helperText={
                                !isFromValid
                                    ? t('finance:invalid_period_date')
                                    : isFromValid && isToValid && !isRangeValid
                                      ? t('finance:invalid_period_range')
                                      : undefined
                            }
                            onChange={(from) => setDateDraft((value) => ({...value, from}))}
                            t={t}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FinanceDateField
                            label={t('finance:period_to')}
                            value={dateDraft.to}
                            error={!isToValid || (isFromValid && isToValid && !isRangeValid)}
                            helperText={
                                !isToValid
                                    ? t('finance:invalid_period_date')
                                    : isFromValid && isToValid && !isRangeValid
                                      ? t('finance:invalid_period_range')
                                      : undefined
                            }
                            onChange={(to) => setDateDraft((value) => ({...value, to}))}
                            t={t}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            label={t('finance:group_by')}
                            value={period.groupBy}
                            onChange={(event) => setPeriod((value) => ({...value, groupBy: event.target.value as FinanceGroupBy}))}
                        >
                            <MenuItem value="DAY">{t('finance:group_day')}</MenuItem>
                            <MenuItem value="WEEK">{t('finance:group_week')}</MenuItem>
                            <MenuItem value="MONTH">{t('finance:group_month')}</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                const currentPeriod = getInitialPeriod();
                                setDateDraft({from: toFinanceDateDisplay(currentPeriod.from), to: toFinanceDateDisplay(currentPeriod.to)});
                                setPeriod(currentPeriod);
                            }}
                        >
                            {t('finance:current_month')}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {(summaryQuery.isError || transactionsQuery.isError) && (
                <Alert severity="warning" sx={{mb: 3}}>
                    {getApiErrorMessage(summaryQuery.error || transactionsQuery.error, t)}
                </Alert>
            )}

            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12}>
                    <FinanceChart
                        series={summaryQuery.data?.series}
                        totals={totals}
                        loading={summaryQuery.isFetching}
                        locale={locale}
                        currency={currency}
                        t={t}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12}>
                    <Paper sx={{p: {xs: 2, md: 3}, height: '100%'}}>
                        <Typography variant="h3">{t('finance:period_details')}</Typography>
                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)'}, gap: 1.5, mt: 2, '& > .MuiStack-root': {alignItems: 'flex-start', flexDirection: 'column', gap: 0.5, p: 1.5, bgcolor: 'background.default', borderRadius: 2}}}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">{t('finance:gross_profit')}</Typography>
                                <Typography fontWeight={700}>{currency(totals?.grossProfit ?? 0)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">{t('finance:accounting_expenses')}</Typography>
                                <Typography fontWeight={700}>{currency(totals?.accountingExpenses ?? 0)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">{t('finance:total_purchases')}</Typography>
                                <Typography fontWeight={700}>{currency(totals?.totalPurchases ?? 0)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">{t('finance:paid_expenses')}</Typography>
                                <Typography fontWeight={700}>{currency(totals?.paidExpenses ?? 0)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">{t('finance:net_cash_flow')}</Typography>
                                <Typography fontWeight={700} color={(totals?.netCashFlow ?? 0) < 0 ? 'error.main' : 'text.primary'}>{currency(totals?.netCashFlow ?? 0)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">{t('finance:margin')}</Typography>
                                <Typography fontWeight={700}>{(totals?.orderPerformance?.grossMarginPercent ?? 0).toFixed(2)}%</Typography>
                            </Stack>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{p: 3, height: '100%'}}>
                        <Typography variant="h3" sx={{mb: 0.5}}>{t('finance:order_cost_breakdown')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            {t('finance:order_cost_breakdown_hint')}
                        </Typography>
                        <BreakdownRows rows={costRows} total={totals?.orderCost ?? 0} currency={currency} emptyText={t('finance:no_data')}/>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{p: 3, height: '100%'}}>
                        <Typography variant="h3" sx={{mb: 0.5}}>{t('finance:purchases')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            {t('finance:purchases_hint')}
                        </Typography>
                        <BreakdownRows rows={purchaseRows} total={totals?.totalPurchases ?? 0} currency={currency} emptyText={t('finance:no_data')}/>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{p: 3, height: '100%'}}>
                        <Typography variant="h3" sx={{mb: 0.5}}>{t('finance:other_expenses')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            {t('finance:other_expenses_hint')}
                        </Typography>
                        <BreakdownRows
                            rows={summaryQuery.data?.accountingExpenseBreakdown ?? []}
                            total={totals?.accountingExpenses ?? 0}
                            currency={currency}
                            emptyText={t('finance:no_data')}
                        />
                    </Paper>
                </Grid>
            </Grid>

            <FinanceTransactions
                page={transactionsQuery.data}
                pageIndex={page}
                pageSize={pageSize}
                loading={transactionsQuery.isFetching}
                locale={locale}
                currency={currency}
                onPageChange={setPage}
                onPageSizeChange={(value) => {
                    setPageSize(value);
                    setPage(0);
                }}
                onEdit={openEditExpense}
                onDelete={setDeleteTarget}
                t={t}
            />

            <ManualExpenseDialog
                open={expenseDialogOpen}
                expense={selectedExpense}
                defaultAccountingMonth={period.from.slice(0, 7)}
                onClose={() => setExpenseDialogOpen(false)}
                onSaved={(message) => { setNotificationSeverity('success'); setNotification(message); }}
                t={t}
            />

            <Dialog open={!!deleteTarget} onClose={() => !deleteState.isLoading && setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('finance:delete_expense')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('finance:delete_expense_confirmation')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleteState.isLoading}>
                        {t('common:cancel')}
                    </Button>
                    <WriteButton color="error" variant="contained" onClick={confirmDelete} disabled={deleteState.isLoading}>
                        {t('finance:delete')}
                    </WriteButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!notification}
                autoHideDuration={4000}
                onClose={() => setNotification('')}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            ><Alert severity={notificationSeverity} onClose={() => setNotification('')}>{notification}<TraceIdFeedback traceId={notificationTraceId}/></Alert></Snackbar>
        </Box>
    );
};

export default Finances;
